import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";
import { FiPackage, FiPlus } from "react-icons/fi";
import { Page, Badge, Action, Select, request } from "./shared";
import { PageHeader, StateBlock, Skeleton, ConfirmDialog } from "../../components/UI";
import { useAsync } from "../../hooks/useAsync";
import { useLanguage } from "../../contexts/LanguageContext";
import { pluralRu } from "../../lib/format";
import { useAuthStore } from "../../store/authStore";
import AddSkinDialog from "./skins/AddSkinDialog";
import InventoryCard from "./skins/InventoryCard";
import ItemImage from "./skins/ItemImage";
import { FilterChip, ResetButton, SearchBox } from "./skins/parts";
import { Saved, TEAM_CT, TEAM_T, slotOf, splitName } from "./skins/data";

const Grid = styled.ul`
  list-style: none;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 276px), 1fr));
  @media (max-width: 560px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
`;
const Empty = styled.div`
  display: grid;
  justify-items: center;
  gap: 8px;
  text-align: center;
  padding: 48px 20px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  > svg {
    width: 32px;
    height: 32px;
    margin-bottom: 4px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  h2 {
    font-size: 20px;
    line-height: 28px;
  }
  p {
    max-width: 46ch;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .cta {
    margin-top: 12px;
  }
`;
const Notice = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.infoSoft};
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 14px;
  line-height: 20px;
  a {
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;
const Subject = styled.div`
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  padding: 12px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.input};
  .img {
    aspect-ratio: 4 / 3;
  }
  strong {
    display: block;
    overflow-wrap: anywhere;
  }
  span {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;

const Panel = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background: ${(p) => p.theme.colors.surface};
  box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.border};
`;
const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
  .filters {
    display: grid;
    grid-template-columns: 180px minmax(200px, 300px);
    gap: 10px;
    min-width: 0;
  }
  select {
    min-height: 44px;
    padding-block: 9px;
    font-size: 15px;
  }
  @media (max-width: 720px) {
    .filters {
      width: 100%;
      grid-template-columns: minmax(0, 1fr);
    }
  }
`;
const Switch = styled.div`
  display: flex;
  gap: 4px;
  max-width: 100%;
  padding: 4px;
  overflow-x: auto;
  border-radius: 10px;
  background: ${(p) => p.theme.colors.input};
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  button {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 38px;
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    color: ${(p) => p.theme.colors.textSecondary};
    transition:
      color ${(p) => p.theme.motion.fast},
      background ${(p) => p.theme.motion.fast};
    &:hover {
      color: ${(p) => p.theme.colors.text};
    }
    &[aria-pressed="true"] {
      background: ${(p) => p.theme.colors.surfaceHover};
      color: ${(p) => p.theme.colors.text};
      box-shadow: inset 0 -2px 0 ${(p) => p.theme.colors.primary};
    }
    .n {
      min-width: 22px;
      padding: 1px 6px;
      border-radius: 999px;
      font-size: 12px;
      text-align: center;
      background: ${(p) => p.theme.colors.border};
      color: ${(p) => p.theme.colors.textSecondary};
    }
    &[aria-pressed="true"] .n {
      background: ${(p) => p.theme.colors.primarySoft};
      color: ${(p) => p.theme.colors.primary};
    }
  }
`;
const Results = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 32px;
  padding-top: 10px;
  border-top: 1px solid ${(p) => p.theme.colors.border};
  font-size: 14px;
  color: ${(p) => p.theme.colors.textSecondary};
  .count {
    flex: none;
    font-weight: 600;
    strong {
      color: ${(p) => p.theme.colors.text};
    }
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex: 1 1 auto;
  }
  .reset {
    flex: none;
    margin-left: auto;
    &[data-off="true"] {
      visibility: hidden;
    }
  }
  @media (max-width: 560px) {
    flex-wrap: wrap;
    .chips {
      order: 3;
      flex-basis: 100%;
    }
  }
`;
const Loadout = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  background: ${(p) => p.theme.colors.surface};
  box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.border};
  h2 {
    font-size: 13px;
    line-height: 18px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .tiles {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    @media (max-width: 720px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }
  .tile {
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    min-height: 64px;
    padding: 8px 12px 8px 8px;
    border-radius: 10px;
    background: ${(p) => p.theme.colors.input};
    .img {
      aspect-ratio: 4 / 3;
    }
    .ph {
      aspect-ratio: 4 / 3;
      border-radius: 8px;
      background: ${(p) => p.theme.colors.navigation};
      box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.border};
    }
    .label {
      display: block;
      font-size: 12px;
      color: ${(p) => p.theme.colors.textMuted};
    }
    strong {
      display: block;
      font-size: 14px;
      line-height: 19px;
      overflow-wrap: anywhere;
    }
    .sub {
      font-size: 12px;
      color: ${(p) => p.theme.colors.textMuted};
    }
  }
  .tile.empty strong {
    color: ${(p) => p.theme.colors.textMuted};
    font-weight: 500;
  }
`;

type SideFilter = "all" | "t" | "ct" | "none";
type KindFilter = "all" | "weapon" | "knife" | "glove";

export default function Skins() {
  const { t, language } = useLanguage();
  const user = useAuthStore((s) => s.user);
  const inventory = useAsync<{ items: Saved[] }>("inventory", (signal) =>
    request("/skins", undefined, undefined, signal),
  );
  // После изменений список подтягивается напрямую: остаётся на экране без мигания скелетонов.
  const [fresh, setFresh] = useState<Saved[] | null>(null);
  const [dialog, setDialog] = useState<{ initial?: Saved } | null>(null);
  const [deleting, setDeleting] = useState<Saved | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [highlight, setHighlight] = useState("");
  const [side, setSide] = useState<SideFilter>("all");
  const [kind, setKind] = useState<KindFilter>("all");
  const [query, setQuery] = useState("");
  const [toggling, setToggling] = useState("");

  const items = fresh ?? inventory.data?.items ?? [];
  const premium = !!user?.isPremium;
  const onSide = (s: Saved, team: number) => s.teams.includes(team);
  const counts = {
    all: items.length,
    t: items.filter((s) => onSide(s, TEAM_T)).length,
    ct: items.filter((s) => onSide(s, TEAM_CT)).length,
    none: items.filter((s) => s.teams.length === 0).length,
  };
  const needle = query.trim().toLowerCase();
  const sideNo = side === "t" ? TEAM_T : side === "ct" ? TEAM_CT : 0;
  const visible = items.filter(
    (s) =>
      (kind === "all" || s.item.kind === kind) &&
      (!needle || s.item.name.toLowerCase().includes(needle)) &&
      (sideNo ? onSide(s, sideNo) : side === "none" ? s.teams.length === 0 : true),
  );
  const sideItems = sideNo ? items.filter((s) => onSide(s, sideNo)) : [];
  const knife = sideItems.find((s) => s.item.kind === "knife");
  const gloves = sideItems.find((s) => s.item.kind === "glove");
  const weapons = sideItems.filter((s) => s.item.kind === "weapon");
  const filtered = side !== "all" || kind !== "all" || !!needle;
  const resetFilters = () => {
    setSide("all");
    setKind("all");
    setQuery("");
  };
  const kindExists = kind === "all" || items.some((s) => s.item.kind === kind);
  const emptyKey =
    needle || (kind !== "all" && kindExists)
      ? "no_results"
      : kind !== "all"
        ? "kind_empty"
        : side === "none"
          ? "none_empty"
          : "side_empty";
  const sideTabs: { key: SideFilter; label: string; full: string }[] = [
    { key: "all", label: t("skins.filter_all"), full: t("skins.filter_all") },
    { key: "t", label: t("skins.view_t"), full: t("skins.status_t") },
    { key: "ct", label: t("skins.view_ct"), full: t("skins.status_ct") },
    { key: "none", label: t("skins.filter_none"), full: t("skins.filter_none") },
  ];
  const sideChip = sideTabs.find((x) => x.key === side)?.label || "";
  const teamName = (team: number) => (team === TEAM_T ? "T" : "CT");
  const tile = (label: string, s?: Saved) => (
    <div className={s ? "tile" : "tile empty"}>
      {s ? (
        <div className="img">
          <ItemImage src={s.item.image} alt="" />
        </div>
      ) : (
        <div className="ph" />
      )}
      <div>
        <span className="label">{label}</span>
        <strong>{s ? splitName(s.item.name).skin || s.item.name : t("skins.loadout_empty")}</strong>
        {s && splitName(s.item.name).skin && (
          <span className="sub">{splitName(s.item.name).model}</span>
        )}
      </div>
    </div>
  );
  const count = (n: number) =>
    language === "ru"
      ? `${n} ${pluralRu(n, ["предмет", "предмета", "предметов"])}`
      : `${n} ${n === 1 ? "item" : "items"}`;

  async function refresh() {
    try {
      const d = await request<{ items: Saved[] }>("/skins");
      setFresh(d.items);
    } catch {
      inventory.reload();
    }
  }

  function saved(id: string) {
    setDialog(null);
    refresh();
    setHighlight(id);
    toast.success(t("skins.saved"));
    window.setTimeout(() => setHighlight(""), 4000);
  }

  async function remove() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError("");
    try {
      await request("/skins/" + deleting.id, undefined, "DELETE");
      setDeleting(null);
      await refresh();
      toast.success(t("skins.deleted"));
    } catch (e) {
      setDeleteError((e as Error).message);
    } finally {
      setDeleteBusy(false);
    }
  }

  /** Переключает сторону одного предмета. Ошибку отдаёт вызывающему: меню покажет «Повторить». */
  async function toggleSide(s: Saved, team: number) {
    const on = s.teams.includes(team);
    const next = on ? s.teams.filter((x) => x !== team) : [...s.teams, team];
    const other = on
      ? undefined
      : items.find((o) => o.id !== s.id && slotOf(o.item) === slotOf(s.item) && o.teams.includes(team));
    setToggling(s.id);
    try {
      await request("/skins", {
        id: s.id,
        catalogId: s.item.id,
        teams: next,
        stickers: s.stickers || [],
        charm: s.charm || "",
        condition: "",
      });
      await refresh();
      const name = splitName(s.item.name).skin || s.item.name;
      toast.success(
        on
          ? t("skins.unequipped_toast", { name, side: teamName(team) })
          : other
            ? t("skins.equipped_replaced_toast", {
                name,
                side: teamName(team),
                other: other.item.name,
              })
            : t("skins.equipped_toast", { name, side: teamName(team) }),
      );
    } finally {
      setToggling("");
    }
  }

  const add = (
    <Action
      type="button"
      $variant="primary"
      disabled={!premium}
      onClick={() => setDialog({})}
    >
      <FiPlus aria-hidden="true" />
      {t("skins.add")}
    </Action>
  );

  return (
    <Page>
      <PageHeader
        title={t("nav.inventory")}
        description={t("skins.set_note")}
        badge={inventory.status === "success" || fresh ? <Badge>{count(items.length)}</Badge> : undefined}
        actions={premium && items.length > 0 ? add : undefined}
      />

      {!premium && items.length > 0 && (
        <Notice>
          {t("skins.need_premium")}
          <Link to="/subscription">{t("skins.premium_cta")}</Link>
        </Notice>
      )}

      {inventory.status === "error" && !inventory.data && !fresh ? (
        <StateBlock
          kind="error"
          title={t("skins.load_error")}
          text={inventory.error}
          onAction={inventory.reload}
        />
      ) : inventory.status !== "success" && !inventory.data && !fresh ? (
        <Grid role="status" aria-busy="true" aria-label={t("common.loading")}>
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i}>
              <Skeleton $h={400} $r={14} />
            </li>
          ))}
        </Grid>
      ) : items.length === 0 ? (
        premium ? (
          <Empty>
            <FiPackage aria-hidden="true" />
            <h2>{t("skins.empty_title")}</h2>
            <p>{t("skins.empty_text")}</p>
            <div className="cta">{add}</div>
          </Empty>
        ) : (
          <Empty>
            <FiPackage aria-hidden="true" />
            <h2>{t("skins.premium_title")}</h2>
            <p>{t("skins.premium_text")}</p>
            <Action as={Link} to="/subscription" $variant="primary" className="cta">
              {t("skins.premium_cta")}
            </Action>
          </Empty>
        )
      ) : (
        <>
          <Panel aria-label={t("skins.filter_label")}>
            <Controls>
              <Switch role="group" aria-label={t("skins.view_label")}>
                {sideTabs.map((x) => (
                  <button
                    key={x.key}
                    type="button"
                    aria-pressed={side === x.key}
                    aria-label={`${x.full}: ${counts[x.key]}`}
                    title={x.full}
                    onClick={() => setSide(x.key)}
                  >
                    {x.label}
                    <span className="n">{counts[x.key]}</span>
                  </button>
                ))}
              </Switch>
              <div className="filters">
                <Select
                  aria-label={t("skins.kind")}
                  value={kind}
                  onChange={(e) => setKind(e.target.value as KindFilter)}
                >
                  <option value="all">{t("skins.kind_all")}</option>
                  <option value="weapon">{t("skins.kind.weapon")}</option>
                  <option value="knife">{t("skins.kind.knife")}</option>
                  <option value="glove">{t("skins.kind.glove")}</option>
                </Select>
                <SearchBox
                  label={t("skins.search_inventory")}
                  value={query}
                  onChange={setQuery}
                />
              </div>
            </Controls>
            <Results>
              <span className="count" role="status">
                {t("skins.found_of", { n: visible.length, total: items.length })}
              </span>
              <span className="chips" aria-label={t("skins.active_filters")}>
                {side !== "all" && (
                  <FilterChip
                    removeLabel={t("skins.remove_filter", { value: sideChip })}
                    onRemove={() => setSide("all")}
                  >
                    {sideChip}
                  </FilterChip>
                )}
                {kind !== "all" && (
                  <FilterChip
                    removeLabel={t("skins.remove_filter", { value: t(`skins.kind.${kind}`) })}
                    onRemove={() => setKind("all")}
                  >
                    {t(`skins.kind.${kind}`)}
                  </FilterChip>
                )}
                {needle && (
                  <FilterChip
                    removeLabel={t("skins.remove_filter", { value: query.trim() })}
                    onRemove={() => setQuery("")}
                  >
                    “{query.trim()}”
                  </FilterChip>
                )}
              </span>
              <span className="reset" data-off={!filtered} aria-hidden={!filtered}>
                <ResetButton label={t("skins.reset_all")} onClick={resetFilters} />
              </span>
            </Results>
          </Panel>

          {sideNo > 0 && (
            <Loadout
              aria-label={t("skins.loadout_title", { side: teamName(sideNo) })}
            >
              <h2>{t("skins.loadout_title", { side: teamName(sideNo) })}</h2>
              <div className="tiles">
                {tile(t("skins.loadout_knife"), knife)}
                {tile(t("skins.loadout_gloves"), gloves)}
                <div className={weapons.length ? "tile" : "tile empty"}>
                  {weapons.length ? (
                    <div className="img">
                      <ItemImage src={weapons[0].item.image} alt="" />
                    </div>
                  ) : (
                    <div className="ph" />
                  )}
                  <div>
                    <span className="label">{t("skins.loadout_weapons")}</span>
                    <strong>
                      {weapons.length
                        ? t("skins.loadout_weapons_count", { n: weapons.length })
                        : t("skins.loadout_empty")}
                    </strong>
                    {weapons.length > 0 && (
                      <span className="sub">
                        {weapons.map((w) => splitName(w.item.name).model).join(", ")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Loadout>
          )}

          {visible.length === 0 ? (
            <Empty>
              <FiPackage aria-hidden="true" />
              <h2>{t("skins.no_results_title")}</h2>
              <p>
                {emptyKey === "kind_empty"
                  ? t("skins.kind_empty_title", { kind: t(`skins.kind_plural.${kind}`) })
                  : t(`skins.${emptyKey}_text`)}
              </p>
              {filtered && (
                <Action type="button" className="cta" onClick={resetFilters}>
                  {t("skins.reset_all")}
                </Action>
              )}
            </Empty>
          ) : (
            <Grid>
              {visible.map((s) => (
                <InventoryCard
                  key={s.id}
                  saved={s}
                  inventory={items}
                  highlight={highlight === s.id}
                  busy={toggling !== ""}
                  onToggleSide={(team) => toggleSide(s, team)}
                  onEdit={() => setDialog({ initial: s })}
                  onDelete={() => {
                    setDeleteError("");
                    setDeleting(s);
                  }}
                />
              ))}
            </Grid>
          )}
        </>
      )}

      {dialog && (
        <AddSkinDialog
          initial={dialog.initial}
          inventory={items}
          defaultTeams={sideNo ? [sideNo] : [TEAM_T, TEAM_CT]}
          isPremium={premium}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={t("skins.delete_title_named", { name: splitName(deleting.item.name).skin || deleting.item.name })}
          description={t("skins.delete_text")}
          confirmLabel={t("skins.delete")}
          busy={deleteBusy}
          error={deleteError}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
          subject={
            <Subject>
              <div className="img">
                <ItemImage src={deleting.item.image} alt="" />
              </div>
              <div>
                <strong>{splitName(deleting.item.name).skin || deleting.item.name}</strong>
                <span>
                  {splitName(deleting.item.name).model}
                  {deleting.teams.length > 0 &&
                    " · " +
                      deleting.teams.map((x) => (x === TEAM_T ? "T" : "CT")).join(" / ")}
                </span>
              </div>
            </Subject>
          }
        />
      )}
    </Page>
  );
}
